"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

/**
 * Bot/AI/operator message avatar. The visitor sees this circle next
 * to every outbound bubble + the typing indicator.
 *
 * Three rendering modes, picked in order:
 *   1. **Gaia (AI/bot)** — wexio-branded gradient circle. Identifies
 *      the AI assistant regardless of operator branding, so visitors
 *      always know when they're talking to a bot vs. a human.
 *   2. **Operator logo** — the org's uploaded mark, filling the circle.
 *   3. **Monogram fallback** — first letter of the org name. Used when
 *      the operator hasn't supplied a logo (would rather not surface
 *      the host product's mark inside message bubbles).
 */
export function BotAvatar({
  size,
  senderKind,
  senderName,
  senderAvatarUrl,
  aiAssistantAvatarUrl,
  organizationLogoUrl,
  organizationName,
  className,
}: {
  size: number;
  /** Drives the AI branch. `ai` + `bot` resolve to either the
   *  operator-configured `aiAssistantAvatarUrl` (if any) or
   *  *nothing* — the previous Wexio-mark fallback is gone so the
   *  operator explicitly opts into surfacing a bot identity by
   *  uploading an avatar. */
  senderKind?: string;
  /** Operator's display name. Used for the monogram fallback when
   *  the operator has no personal avatar — visitors see "RL" for
   *  "Ralph J. Lesperance" instead of the org's first initial,
   *  which would otherwise read like the same "W" for every human
   *  in a multi-operator team. */
  senderName?: string;
  /** Per-sender avatar URL — operator who uploaded a personal photo.
   *  Wins over the org logo when present. AI/bot senders never carry
   *  a personal avatar; the AI branch above handles them. */
  senderAvatarUrl?: string;
  /** Operator-uploaded AI assistant avatar. Plumbed from
   *  `config.messenger.aiAssistantAvatar`. When `null` / `undefined`,
   *  AI bubbles render *without* an avatar (caller is expected to
   *  collapse the avatar slot in its layout). */
  aiAssistantAvatarUrl?: string | null;
  organizationLogoUrl?: string | null;
  organizationName?: string;
  className?: string;
}) {
  const t = useTranslations("messages");
  const sizeStyle = { width: size, height: size };

  // AI / bot branch.
  //   - If the operator uploaded an AI assistant avatar via the
  //     dashboard's messenger settings, render it.
  //   - If not, return `null`. Caller is expected to *omit* the
  //     avatar from its layout (the message bubble's flex row
  //     should drop the column entirely so the bubble doesn't
  //     leave an empty slot). This is the explicit-opt-in shape
  //     the operator brief asked for.
  if (senderKind === "ai" || senderKind === "bot") {
    if (!aiAssistantAvatarUrl) {
      // No operator-configured AI avatar → render NOTHING (not an empty
      // spacer): the caller collapses the avatar column so the AI bubble sits
      // flush-left instead of leaving a blank gap where an avatar would be.
      return null;
    }
    return (
      <span
        style={sizeStyle}
        className={cn(
          "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-wx-bg-elevated shadow-sm",
          className,
        )}
        role="img"
        aria-label={t("aiAssistant")}
      >
        {/* biome-ignore lint/performance/noImgElement: operator-uploaded avatar of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img
          src={aiAssistantAvatarUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
          decoding="async"
        />
      </span>
    );
  }

  if (senderAvatarUrl) {
    return (
      <span style={sizeStyle} className={cn("shrink-0 overflow-hidden rounded-full", className)}>
        {/* biome-ignore lint/performance/noImgElement: operator-uploaded avatar of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img src={senderAvatarUrl} alt="" className="h-full w-full object-cover" />
      </span>
    );
  }

  // Monogram fallback. Prefer the *operator's* name initials (one
  // or two characters drawn from the leading word + the surname,
  // e.g. "RL" for "Ralph J. Lesperance") over the org name. The
  // previous version always painted the org's first letter, so
  // every human on a multi-operator team showed up as the same
  // "W" / "A" / etc — making them visually indistinguishable. The
  // org logo is still a stronger anchor than a 1-letter monogram,
  // so we only fall back to the org-logo branch if the message
  // has no usable sender name attached.
  const operatorInitials = senderName ? initialsOf(senderName) : "";
  if (!operatorInitials && organizationLogoUrl) {
    return (
      <span style={sizeStyle} className={cn("shrink-0 overflow-hidden rounded-full", className)}>
        {/* biome-ignore lint/performance/noImgElement: org logo of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img
          src={organizationLogoUrl}
          alt={organizationName ?? ""}
          className="h-full w-full object-cover"
        />
      </span>
    );
  }
  const initials = operatorInitials || (organizationName ?? "").trim().charAt(0).toUpperCase();
  return (
    <span
      style={sizeStyle}
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-wx-bg-elevated text-[0.7em] font-semibold text-wx-fg-muted",
        className,
      )}
    >
      {initials || ""}
    </span>
  );
}

/**
 * Compact initials extractor for the monogram avatar. Picks the
 * first letter of the *first* word and the first letter of the
 * *last* word — handles middle names + initials gracefully:
 *   "Ralph J. Lesperance"   → "RL"
 *   "John Doe"              → "JD"
 *   "Cher"                  → "C"
 *   "  "                    → ""
 * Always returned uppercase. Drops anything non-alphanumeric.
 */
function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((w) => w.length > 0);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return words[0].charAt(0).toUpperCase() + words[words.length - 1].charAt(0).toUpperCase();
}
